<!-- eslint-disable -->
<template>
  <v-container class="group-container">
    <!-- Loading -->
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="primary"
      class="d-block mx-auto my-8"
    ></v-progress-circular>

    <!-- Access Denied for Private Groups -->
    <v-card v-else-if="accessDenied" elevation="2" class="text-center pa-8">
      <v-icon size="80" color="grey" class="mb-4">mdi-lock</v-icon>
      <h2 class="text-h5 mb-2">{{ $t('groups.privateGroup') }}</h2>
      <p class="text-grey mb-4">{{ $t('groups.privateGroupMessage') }}</p>
      <v-btn color="primary" :to="'/groups'">
        <v-icon start>mdi-arrow-left</v-icon>
        {{ $t('groups.backToGroups') }}
      </v-btn>
    </v-card>

    <!-- Group Not Found -->
    <v-card v-else-if="notFound" elevation="2" class="text-center pa-8">
      <v-icon size="80" color="grey" class="mb-4">mdi-account-group-outline</v-icon>
      <h2 class="text-h5 mb-2">{{ $t('groups.notFound') }}</h2>
      <p class="text-grey mb-4">{{ $t('groups.notFoundMessage') }}</p>
      <v-btn color="primary" :to="'/groups'">
        <v-icon start>mdi-arrow-left</v-icon>
        {{ $t('groups.backToGroups') }}
      </v-btn>
    </v-card>

    <template v-else-if="group">
      <!-- Group Header -->
      <v-card elevation="2" class="mb-4">
        <v-img
          :src="group.coverImageUrl || defaultCover"
          height="200"
          cover
        >
          <!-- Camera button for changing cover (admin only) -->
          <v-btn
            v-if="group.userRole === 'admin'"
            icon
            color="white"
            variant="flat"
            class="cover-camera-btn"
            @click="triggerCoverUpload"
          >
            <v-icon>mdi-camera</v-icon>
          </v-btn>
          <input
            ref="coverInput"
            type="file"
            accept="image/*"
            style="display: none;"
            @change="uploadCoverImage"
          />

          <div class="group-header-overlay d-flex flex-column justify-end pa-4">
            <div class="d-flex align-center">
              <div class="flex-grow-1">
                <h1 class="text-h4 text-white">{{ group.groupName }}</h1>
                <div class="text-white text-opacity-80">
                  <v-chip
                    v-if="!group.isPublic"
                    color="grey"
                    size="small"
                    class="mr-2"
                  >
                    <v-icon start size="small">mdi-lock</v-icon>
                    {{ $t('groups.private') }}
                  </v-chip>
                  {{ group.memberCount }} {{ $t('groups.members') }} • {{ group.postCount }} {{ $t('groups.postsCount') }}
                </div>
              </div>
              
              <!-- Actions -->
              <div class="d-flex" style="gap: 0.5rem;">
                <v-btn
                  v-if="!group.userRole"
                  color="white"
                  @click="joinGroup"
                  :loading="joining"
                >
                  <v-icon start>mdi-plus</v-icon>
                  {{ $t('groups.join') }}
                </v-btn>
                
                <v-menu v-if="group.userRole === 'admin'">
                  <template v-slot:activator="{ props: menuProps }">
                    <v-btn icon v-bind="menuProps" color="white" variant="text">
                      <v-icon>mdi-cog</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="triggerCoverUpload">
                      <template v-slot:prepend>
                        <v-icon>mdi-image</v-icon>
                      </template>
                      <v-list-item-title>{{ $t('groups.changeCover') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="showEditDialog = true">
                      <template v-slot:prepend>
                        <v-icon>mdi-pencil</v-icon>
                      </template>
                      <v-list-item-title>{{ $t('common.edit') }}</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item @click="deleteGroup" class="text-error">
                      <template v-slot:prepend>
                        <v-icon color="error">mdi-delete</v-icon>
                      </template>
                      <v-list-item-title>{{ $t('common.delete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </v-img>
        
        <v-card-text v-if="group.groupDescription">
          {{ group.groupDescription }}
        </v-card-text>
      </v-card>

      <!-- Tabs -->
      <v-tabs v-model="tab" bg-color="white" color="primary" class="mb-4">
        <v-tab value="posts">{{ $t('groups.postsTab') }}</v-tab>
        <v-tab value="members">{{ $t('groups.membersTab') }}</v-tab>
        <v-tab v-if="!group.isPublic && (group.userRole === 'admin' || group.userRole === 'moderator')" value="invites">
          {{ $t('groups.invitesTab') }}
          <v-badge v-if="pendingInvites.length > 0" :content="pendingInvites.length" color="primary" inline class="ml-2"></v-badge>
        </v-tab>
      </v-tabs>

      <v-row>
        <v-col :cols="tab === 'posts' ? 8 : 12" :md="tab === 'posts' ? 8 : 12">
          <v-window v-model="tab">
            <!-- Posts Tab -->
            <v-window-item value="posts">
              <div v-if="group.userRole || group.isPublic">
                <!-- Posts (no more inline PostForm here) -->
                <v-progress-circular
                  v-if="loadingPosts"
                  indeterminate
                  color="primary"
                  class="d-block mx-auto my-4"
                ></v-progress-circular>
                
                <div v-else-if="posts.length > 0" class="posts-feed">
                  <post-card
                    v-for="post in posts"
                    :key="post.postId"
                    :post="post"
                    :read-only="!group.userRole"
                    @deleted="handlePostDeleted"
                    @updated="handlePostUpdated"
                    class="mb-4"
                  />
                </div>
                
                <v-alert v-else type="info">
                  {{ $t('groups.noPosts') }}
                </v-alert>
              </div>

              <v-alert v-else type="info">
                {{ $t('groups.joinToSee') }}
              </v-alert>
            </v-window-item>

            <!-- Members Tab -->
            <v-window-item value="members">
              <v-card elevation="2">
                <v-list>
                  <v-list-item
                    v-for="member in members"
                    :key="member.userId"
                  >
                    <template v-slot:prepend>
                      <v-avatar 
                        color="primary" 
                        size="40"
                        class="cursor-pointer"
                        @click="$router.push(`/profile/${member.username}`)"
                      >
                        <v-img v-if="member.avatarUrl" :src="member.avatarUrl"></v-img>
                        <span v-else>{{ member.displayName?.charAt(0) || 'U' }}</span>
                      </v-avatar>
                    </template>

                    <v-list-item-title 
                      class="cursor-pointer"
                      @click="$router.push(`/profile/${member.username}`)"
                    >
                      {{ member.displayName || member.username }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      @{{ member.username }}
                    </v-list-item-subtitle>

                    <template v-slot:append>
                      <v-chip
                        :color="getRoleColor(member.role)"
                        size="small"
                        class="mr-2"
                      >
                        {{ member.role }}
                      </v-chip>
                      
                      <!-- Admin actions -->
                      <v-menu v-if="group.userRole === 'admin' && member.userId !== currentUser?.userId">
                        <template v-slot:activator="{ props: menuProps }">
                          <v-btn icon v-bind="menuProps" variant="text" size="small">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="updateMemberRole(member, 'member')">
                            <v-list-item-title>{{ $t('groups.setMember') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="updateMemberRole(member, 'moderator')">
                            <v-list-item-title>{{ $t('groups.setModerator') }}</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="updateMemberRole(member, 'admin')">
                            <v-list-item-title>{{ $t('groups.setAdmin') }}</v-list-item-title>
                          </v-list-item>
                          <v-divider></v-divider>
                          <v-list-item @click="removeMember(member)" class="text-error">
                            <v-list-item-title>{{ $t('groups.removeMember') }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-window-item>

            <!-- Invites Tab (for private groups, admin/moderator only) -->
            <v-window-item value="invites">
              <v-card elevation="2" class="mb-4">
                <v-card-title>{{ $t('groups.inviteMembers') }}</v-card-title>
                <v-card-text>
                  <v-autocomplete
                    v-model="selectedUserToInvite"
                    :items="searchedUsers"
                    :loading="searchingUsers"
                    :search="userSearchQuery"
                    @update:search="searchUsersToInvite"
                    item-title="displayName"
                    item-value="userId"
                    :label="$t('groups.searchUsers')"
                    variant="outlined"
                    return-object
                    clearable
                    hide-no-data
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <template v-slot:prepend>
                          <v-avatar color="primary" size="32">
                            <v-img v-if="item.raw.avatarUrl" :src="item.raw.avatarUrl"></v-img>
                            <span v-else>{{ item.raw.displayName?.charAt(0) || 'U' }}</span>
                          </v-avatar>
                        </template>
                        <v-list-item-subtitle>@{{ item.raw.username }}</v-list-item-subtitle>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  
                  <v-btn 
                    color="primary" 
                    @click="inviteUser" 
                    :disabled="!selectedUserToInvite"
                    :loading="invitingUser"
                  >
                    <v-icon start>mdi-account-plus</v-icon>
                    {{ $t('groups.inviteMember') }}
                  </v-btn>
                </v-card-text>
              </v-card>

              <!-- Pending Invites -->
              <v-card elevation="2">
                <v-card-title>{{ $t('groups.pendingInvites') }}</v-card-title>
                <v-card-text v-if="pendingInvites.length === 0" class="text-grey">
                  {{ $t('groups.noInvites') }}
                </v-card-text>
                <v-list v-else>
                  <v-list-item
                    v-for="invite in pendingInvites"
                    :key="invite.inviteId"
                  >
                    <template v-slot:prepend>
                      <v-avatar color="primary" size="40">
                        <v-img v-if="invite.avatarUrl" :src="invite.avatarUrl"></v-img>
                        <span v-else>{{ invite.displayName?.charAt(0) || 'U' }}</span>
                      </v-avatar>
                    </template>

                    <v-list-item-title>{{ invite.displayName }}</v-list-item-title>
                    <v-list-item-subtitle>
                      @{{ invite.username }} • {{ $t('groups.invitedBy') }} {{ invite.invitedByDisplayName }}
                    </v-list-item-subtitle>

                    <template v-slot:append>
                      <v-btn 
                        variant="text" 
                        color="error" 
                        size="small"
                        @click="cancelInvite(invite)"
                      >
                        {{ $t('groups.cancelInvite') }}
                      </v-btn>
                    </template>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-window-item>
          </v-window>
        </v-col>

        <!-- Sidebar (only on posts tab) -->
        <v-col v-if="tab === 'posts'" cols="4" class="d-none d-md-block">
          <v-card elevation="2">
            <v-card-title>{{ $t('groups.about') }}</v-card-title>
            <v-card-text>
              <div v-if="group.groupDescription" class="mb-4">
                {{ group.groupDescription }}
              </div>
              
              <div class="text-caption text-grey mb-2">
                <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                {{ $t('groups.createdOn') }} {{ formatDate(group.created_at) }}
              </div>
              
              <div class="text-caption text-grey">
                <v-icon size="small" class="mr-1">mdi-account</v-icon>
                {{ $t('groups.createdBy') }} {{ group.creatorDisplayName }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ===== FLOATING COMPOSER BAR ===== -->
      <!-- Only visible on posts tab when user is a member -->
      <teleport to="body">
        <transition name="composer-backdrop">
          <div
            v-if="composerExpanded && group?.userRole && tab === 'posts'"
            class="composer-backdrop"
            @click="collapseComposer"
          ></div>
        </transition>

        <div
          v-if="group?.userRole && tab === 'posts'"
          class="composer-dock"
          :class="{ 'composer-dock--expanded': composerExpanded }"
        >
          <!-- Collapsed bar: teaser prompt -->
          <div
            v-if="!composerExpanded"
            class="composer-collapsed"
            @click="expandComposer"
          >
            <v-avatar color="primary" size="32" class="mr-3">
              <span>{{ currentUser?.displayName?.charAt(0) || 'U' }}</span>
            </v-avatar>
            <span class="composer-placeholder">{{ $t('wall.placeholder') }}</span>
            <v-spacer></v-spacer>
            <v-icon color="primary" size="20">mdi-pencil</v-icon>
          </div>

          <!-- Expanded: full PostForm -->
          <div v-else class="composer-expanded">
            <div class="composer-header d-flex align-center mb-2">
              <span class="text-subtitle-2 font-weight-bold">
                {{ $t('groups.newPost') || $t('wall.placeholder') }}
              </span>
              <v-spacer></v-spacer>
              <v-btn
                icon
                size="small"
                variant="text"
                @click="collapseComposer"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </div>
            <post-form
              ref="floatingPostForm"
              :group-id="group.groupId"
              :autofocus="true"
              @posted="onPostCreated"
            />
          </div>
        </div>
      </teleport>
    </template>

    <!-- Edit Group Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('groups.editGroup') }}</v-card-title>
        <v-card-text>
          <v-form ref="editFormRef">
            <v-text-field
              v-model="editGroup.groupName"
              :label="$t('groups.groupName')"
              variant="outlined"
              class="mb-3"
            ></v-text-field>

            <v-textarea
              v-model="editGroup.groupDescription"
              :label="$t('groups.description')"
              variant="outlined"
              rows="3"
              class="mb-3"
            ></v-textarea>

            <v-checkbox
              v-model="editGroup.isPublic"
              :label="$t('groups.publicGroup')"
              hide-details
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showEditDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="primary" @click="saveGroup" :loading="saving">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Dialog -->
    <confirm-dialog
      ref="confirmDialog"
      :title="$t('common.delete')"
      :message="$t('groups.deleteConfirm')"
      :confirmText="$t('common.delete')"
      :cancelText="$t('common.cancel')"
      confirmColor="error"
      icon="mdi-delete-alert"
    />
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import PostCard from '../components/PostCard.vue'
import PostForm from '../components/PostForm.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { t } = useI18n()

const loading = ref(true)
const group = ref(null)
const posts = ref([])
const members = ref([])
const loadingPosts = ref(false)
const tab = ref('posts')
const accessDenied = ref(false)
const notFound = ref(false)

const joining = ref(false)
const showEditDialog = ref(false)
const saving = ref(false)
const confirmDialog = ref(null)
const coverInput = ref(null)
const uploadingCover = ref(false)

// Floating composer state
const composerExpanded = ref(false)
const floatingPostForm = ref(null)

// Invite-related state
const pendingInvites = ref([])
const selectedUserToInvite = ref(null)
const searchedUsers = ref([])
const searchingUsers = ref(false)
const userSearchQuery = ref('')
const invitingUser = ref(false)

const editGroup = reactive({
  groupName: '',
  groupDescription: '',
  isPublic: true
})

const currentUser = computed(() => store.getters['auth/user'])
const defaultCover = computed(() => `https://picsum.photos/seed/${route.params.id}/800/200`)

const getRoleColor = (role) => {
  switch (role) {
    case 'admin': return 'error'
    case 'moderator': return 'warning'
    default: return 'primary'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

// --- Floating composer methods ---
const expandComposer = async () => {
  composerExpanded.value = true
  // Focus the textarea inside PostForm after it renders
  await nextTick()
  floatingPostForm.value?.focus?.()
}

const collapseComposer = () => {
  composerExpanded.value = false
}

const onPostCreated = () => {
  composerExpanded.value = false
  loadPosts()
}

// Close composer on Escape key
const handleEscKey = (e) => {
  if (e.key === 'Escape' && composerExpanded.value) {
    collapseComposer()
  }
}

// --- Data loading (unchanged) ---
const loadGroup = async () => {
  loading.value = true
  accessDenied.value = false
  notFound.value = false
  
  try {
    const response = await api.get(`/groups/${route.params.id}`)
    group.value = response.data.group
    
    // Pre-fill edit form
    editGroup.groupName = group.value.groupName
    editGroup.groupDescription = group.value.groupDescription
    editGroup.isPublic = group.value.isPublic
    
    // Load posts and members if user has access
    if (group.value.userRole || group.value.isPublic) {
      loadPosts()
      loadMembers()
      if (!group.value.isPublic && ['admin', 'moderator'].includes(group.value.userRole)) {
        loadInvites()
      }
    }
  } catch (error) {
    console.error('Error loading group:', error)
    if (error.response?.status === 403) {
      accessDenied.value = true
    } else if (error.response?.status === 404) {
      notFound.value = true
    }
  } finally {
    loading.value = false
  }
}

const loadPosts = async () => {
  loadingPosts.value = true
  try {
    const response = await api.get(`/groups/${route.params.id}/posts`)
    posts.value = response.data.posts || []
  } catch (error) {
    console.error('Error loading posts:', error)
  } finally {
    loadingPosts.value = false
  }
}

const loadMembers = async () => {
  try {
    const response = await api.get(`/groups/${route.params.id}/members`)
    members.value = response.data.members || []
  } catch (error) {
    console.error('Error loading members:', error)
  }
}

const loadInvites = async () => {
  try {
    const response = await api.get(`/groups/${route.params.id}/invites`)
    pendingInvites.value = response.data.invites || []
  } catch (error) {
    console.error('Error loading invites:', error)
  }
}

let searchTimeout = null
const searchUsersToInvite = async (query) => {
  userSearchQuery.value = query
  if (!query || query.length < 2) {
    searchedUsers.value = []
    return
  }
  
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(async () => {
    searchingUsers.value = true
    try {
      const response = await api.get('/users', { params: { search: query } })
      const memberIds = members.value.map(m => m.userId)
      const inviteIds = pendingInvites.value.map(i => i.invitedUserId)
      searchedUsers.value = (response.data.users || []).filter(
        u => !memberIds.includes(u.userId) && !inviteIds.includes(u.userId)
      )
    } catch (error) {
      console.error('Error searching users:', error)
    } finally {
      searchingUsers.value = false
    }
  }, 300)
}

const inviteUser = async () => {
  if (!selectedUserToInvite.value) return
  
  invitingUser.value = true
  try {
    await api.post(`/groups/${route.params.id}/invites`, {
      userId: selectedUserToInvite.value.userId
    })
    await loadInvites()
    selectedUserToInvite.value = null
    searchedUsers.value = []
  } catch (error) {
    console.error('Error inviting user:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    invitingUser.value = false
  }
}

const cancelInvite = async (invite) => {
  try {
    await api.delete(`/groups/${route.params.id}/invites`, {
      params: { userId: invite.invitedUserId }
    })
    await loadInvites()
  } catch (error) {
    console.error('Error cancelling invite:', error)
  }
}

const joinGroup = async () => {
  joining.value = true
  try {
    const response = await api.post(`/groups/${route.params.id}/members`)
    if (response.data.success) {
      loadGroup()
    }
  } catch (error) {
    console.error('Error joining group:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    joining.value = false
  }
}

const triggerCoverUpload = () => {
  coverInput.value.click()
}

const uploadCoverImage = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  if (!file.type.startsWith('image/')) {
    alert(t('groups.invalidImageType'))
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    alert(t('groups.imageTooLarge'))
    return
  }

  uploadingCover.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)

    const uploadResponse = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (uploadResponse.data.success) {
      const response = await api.put(`/groups/${route.params.id}`, {
        ...editGroup,
        coverImageUrl: uploadResponse.data.imageUrl
      })

      if (response.data.success) {
        group.value.coverImageUrl = uploadResponse.data.imageUrl
      }
    }
  } catch (error) {
    console.error('Error uploading cover:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    uploadingCover.value = false
    coverInput.value.value = ''
  }
}

const saveGroup = async () => {
  saving.value = true
  try {
    const response = await api.put(`/groups/${route.params.id}`, editGroup)
    if (response.data.success) {
      showEditDialog.value = false
      loadGroup()
    }
  } catch (error) {
    console.error('Error saving group:', error)
    alert(error.response?.data?.error || t('common.error'))
  } finally {
    saving.value = false
  }
}

const deleteGroup = async () => {
  const confirmed = await confirmDialog.value.open()
  if (!confirmed) return
  
  try {
    const response = await api.delete(`/groups/${route.params.id}`)
    if (response.data.success) {
      router.push('/groups')
    }
  } catch (error) {
    console.error('Error deleting group:', error)
    alert(error.response?.data?.error || t('common.error'))
  }
}

const updateMemberRole = async (member, role) => {
  try {
    const response = await api.put(`/groups/${route.params.id}/members`, {
      userId: member.userId,
      role: role
    })
    if (response.data.success) {
      loadMembers()
    }
  } catch (error) {
    console.error('Error updating member:', error)
    alert(error.response?.data?.error || t('common.error'))
  }
}

const removeMember = async (member) => {
  if (!confirm(t('groups.removeMemberConfirm', { name: member.displayName }))) return
  
  try {
    const response = await api.delete(`/groups/${route.params.id}/members?userId=${member.userId}`)
    if (response.data.success) {
      loadMembers()
    }
  } catch (error) {
    console.error('Error removing member:', error)
    alert(error.response?.data?.error || t('common.error'))
  }
}

const handlePostDeleted = (postId) => {
  posts.value = posts.value.filter(p => p.postId !== postId)
}

const handlePostUpdated = (updatedPost) => {
  const index = posts.value.findIndex(p => p.postId === updatedPost.postId)
  if (index !== -1) {
    posts.value[index] = updatedPost
  }
}

onMounted(() => {
  loadGroup()
  document.addEventListener('keydown', handleEscKey)
})

// Clean up on unmount
import { onUnmounted } from 'vue'
onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey)
})

watch(() => route.params.id, () => {
  if (route.params.id) {
    composerExpanded.value = false
    loadGroup()
  }
})
</script>

<style scoped>
.group-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 2rem;
  /* Extra bottom padding so posts aren't hidden behind the composer dock */
  padding-bottom: 5rem;
}

.group-header-overlay {
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  height: 100%;
}

.text-opacity-80 {
  opacity: 0.8;
}

.cursor-pointer {
  cursor: pointer;
}

.cover-camera-btn {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: rgba(0, 0, 0, 0.5) !important;
}

.cover-camera-btn:hover {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

/* ==========================================
   FLOATING COMPOSER DOCK
   ========================================== */

/* Semi-transparent backdrop when composer is open */
.composer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1099;
  backdrop-filter: blur(2px);
}

.composer-backdrop-enter-active,
.composer-backdrop-leave-active {
  transition: opacity 0.25s ease;
}
.composer-backdrop-enter-from,
.composer-backdrop-leave-to {
  opacity: 0;
}

/* The dock itself */
.composer-dock {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 680px;
  z-index: 1100;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* --- Collapsed state --- */
.composer-collapsed {
  display: flex;
  align-items: center;
  margin: 0 1rem 1rem;
  padding: 0.625rem 1rem;
  background: #ffffff;
  border-radius: 1.5rem;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  user-select: none;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.composer-collapsed:hover {
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.composer-collapsed:active {
  transform: translateY(0);
}

.composer-placeholder {
  color: #9e9e9e;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- Expanded state --- */
.composer-dock--expanded {
  bottom: 0;
}

.composer-expanded {
  margin: 0 0.5rem 0.5rem;
  padding: 1rem 1.25rem 1.25rem;
  background: #ffffff;
  border-radius: 1rem 1rem 0 0;
  box-shadow:
    0 -4px 24px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  max-height: 70vh;
  overflow-y: auto;
  animation: composer-slide-up 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes composer-slide-up {
  from {
    opacity: 0;
    transform: translateY(2rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.composer-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eeeeee;
}

/* --- Responsive tweaks --- */
@media (max-width: 960px) {
  .composer-dock {
    max-width: 100%;
  }
  .composer-collapsed {
    margin: 0 0.5rem 0.5rem;
  }
  .composer-expanded {
    margin: 0;
    border-radius: 1rem 1rem 0 0;
  }
}
</style>